// Builds MetricTotals from rollup rows plus the ragged-edge raw rows, producing
// exactly what computeMetrics() produces from raw rows alone.
//
// This exists so the browser can stop receiving one row per doctor per day.
// Measured in Chrome at 800-site scale: raw rows are ~2,700 MB of heap against a
// 4,096 MB tab ceiling; the rollup shapes are ~174 MB.
//
// THE INVARIANT EVERYTHING RESTS ON: whole ISO weeks and edge days are disjoint
// by day (see rollupSplit.ts). So sums add, dvmDays / distinctDates / dvmWeeks
// add, and only distinctDvms / siteCount need set unions -- the same doctor or
// site can legitimately appear in both halves.

import { APPT_SLUGS, CANCEL_REASONS, emptyCancelReasons, type MetricTotals } from "./metrics";
import type { SchedulingRow } from "../types/scheduling";
import type { ScopeFilters } from "../types/filters";
import { getWeekKey } from "./periods";

// Rollup rows as they arrive over the wire: numerics come back from pg as
// strings, ids as strings, masks as numbers.
export interface RollupSiteWeekRow {
  location_id: string;
  week_start: string;
  location_name?: string | null;
  country_code?: string | null;
  division_name?: string | null;
  group_name?: string | null;
  [measure: string]: string | number | null | undefined;
}

export interface DvmWeekRow {
  resource_id: string;
  week_start: string;
  location_id: string;
  day_mask: number;
  day_mask_nonzero: number;
  resource_full_name?: string | null;
  country_code?: string | null;
  division_name?: string | null;
  group_name?: string | null;
  // Measures are present only on the wide projection, which the endpoint sends
  // for scope.resourceId or drill-to-DVM.
  [measure: string]: string | number | null | undefined;
}

const num = (v: unknown): number => (v == null ? 0 : Number(v));

// How many days a 7-bit mask marks.
function popcount7(mask: number): number {
  let n = 0;
  for (let b = 0; b < 7; b++) if (mask & (1 << b)) n++;
  return n;
}

interface ScopeableRow {
  country_code?: string | null;
  division_name?: string | null;
  group_name?: string | null;
  location_id?: string | null;
  resource_id?: string | null;
}

// Mirrors filterRowsByScope in metrics.ts. Site-week rows carry no resource_id,
// so a resourceId scope can never be satisfied from them -- which is exactly why
// measuresFromDvmWeeks exists.
export function matchesScope(row: ScopeableRow, scope: ScopeFilters): boolean {
  if (scope.countryCode && row.country_code !== scope.countryCode) return false;
  if (scope.divisionName && row.division_name !== scope.divisionName) return false;
  if (scope.groupName && row.group_name !== scope.groupName) return false;
  if (scope.locationId && row.location_id !== scope.locationId) return false;
  if (scope.resourceId && row.resource_id !== scope.resourceId) return false;
  return true;
}

export interface RollupInput {
  siteWeeks: RollupSiteWeekRow[];
  dvmWeeks: DvmWeekRow[];
  /** Raw rows for the ragged edge days only. Empty for trailing periods. */
  edgeRows: SchedulingRow[];
  scope: ScopeFilters;
  excludeZeroApptDays: boolean;
  /** True when sums must come from dvmWeeks (wide) rather than siteWeeks. */
  measuresFromDvmWeeks: boolean;
}

// siteCount rides along because it is already computed here and cannot be
// recovered from the returned totals: it is the set of sites that SURVIVE the
// filter, which the masks know and a sum does not. Pinned ancestor rows need it,
// and recomputing it at the call site would be a second implementation of a rule
// that took two bugs to get right (see buildDrillFromRollup).
export function sameScope(a: ScopeFilters, b: ScopeFilters): boolean {
  return (
    a.countryCode === b.countryCode &&
    a.divisionName === b.divisionName &&
    a.groupName === b.groupName &&
    a.locationId === b.locationId &&
    a.resourceId === b.resourceId
  );
}

// The ONLY correct way for a hook to total one payload at several scopes.
//
// Wide doctor rows are fetched at exactly one scope -- the request's -- because
// they are ~20x the weight of the narrow ones. Every total taken at a WIDER scope
// (the unscoped NVA benchmark, every pinned ancestor row) must therefore read
// site-week measures instead, or it sums columns the narrow rows do not have and
// gets zero rather than an error. That produced an NVA benchmark ~50x too small.
//
// It was previously each hook's job to remember this, and three of them ended up
// holding a copy of the rule. Copying a formatter is cheap; copying an invariant
// arms the same bug for whoever writes the next tab. So the rule lives here, and
// hooks state only what they have -- the payload's scope and whether its doctor
// rows are wide -- never the conclusion.
export function makeTotalsReader(input: {
  part: Pick<RollupInput, "siteWeeks" | "dvmWeeks" | "edgeRows">;
  /** The scope the payload was FETCHED at, not the one being applied. */
  payloadScope: ScopeFilters;
  /** RollupPayload.wideDvmRows -- whether the doctor rows carry measures at all. */
  wideDvmRows: boolean;
  excludeZeroApptDays: boolean;
}): (scope: ScopeFilters) => MetricTotals & { siteCount: number } {
  const { part, payloadScope, wideDvmRows, excludeZeroApptDays } = input;
  return (scope) =>
    buildTotalsFromRollup({
      ...part,
      scope,
      excludeZeroApptDays,
      measuresFromDvmWeeks: wideDvmRows && sameScope(scope, payloadScope),
    });
}

// Prefer makeTotalsReader. Passing measuresFromDvmWeeks directly is correct only
// when the caller has ALREADY narrowed the rows to the payload's scope --
// buildDrillFromRollup does exactly that and then passes an empty scope, which is
// why the guard cannot simply live in here.
export function buildTotalsFromRollup(input: RollupInput): MetricTotals & { siteCount: number } {
  const { siteWeeks, dvmWeeks, edgeRows, scope, excludeZeroApptDays, measuresFromDvmWeeks } = input;
  const nz = excludeZeroApptDays;

  // ── Sums ──────────────────────────────────────────────────────────────────
  // scheduled/blockoff/appointment have _nonzero variants because
  // excludeZeroApptDays is a row-level filter that pre-summed rows cannot honour
  // after the fact. The 27 kind columns need no variant: a zero-appointment day
  // has zero kind minutes by definition, so excluding it changes nothing.
  let totalScheduledMin = 0;
  let totalWorkedMin = 0;
  let totalBlockoffMin = 0;
  let allSeenMin = 0;
  let totalAvailMin = 0;
  let totalBookableBlocks = 0;
  let totalMultiBookedBlocks = 0;
  let totalCompleted = 0;
  let totalCanceled = 0;
  let totalMedRev = 0;
  let totalCancelLeadDays = 0;
  const canceledByReason = emptyCancelReasons();
  const slugMinutes: Record<string, number> = {};
  for (const s of APPT_SLUGS) slugMinutes[s] = 0;

  const addMeasures = (r: Record<string, unknown>, useNonzeroColumns: boolean) => {
    totalScheduledMin += num(useNonzeroColumns ? r.scheduled_minutes_nonzero : r.scheduled_minutes);
    totalWorkedMin += num(
      useNonzeroColumns ? r.total_worked_minutes_nonzero : r.total_worked_minutes
    );
    totalBlockoffMin += num(useNonzeroColumns ? r.blockoff_minutes_nonzero : r.blockoff_minutes);
    allSeenMin += num(
      useNonzeroColumns ? r.completed_appointment_minutes_nonzero : r.completed_appointment_minutes
    );
    totalAvailMin += num(useNonzeroColumns ? r.available_minutes_nonzero : r.available_minutes);
    // Not just the minutes: a zero-appointment day still carries blocks and
    // cancellations, so excludeZeroApptDays must drop those too. Reading the plain
    // columns here left every block and cancellation total overstated.
    totalBookableBlocks += num(useNonzeroColumns ? r.bookable_blocks_nonzero : r.bookable_blocks);
    totalMultiBookedBlocks += num(
      useNonzeroColumns ? r.multi_booked_blocks_nonzero : r.multi_booked_blocks
    );
    totalCompleted += num(
      useNonzeroColumns ? r.completed_appointments_nonzero : r.completed_appointments
    );
    totalCanceled += num(
      useNonzeroColumns ? r.canceled_appointments_nonzero : r.canceled_appointments
    );
    for (const s of APPT_SLUGS) slugMinutes[s]! += num(r[`appointment_kind_${s}_minutes`]);
    // Revenue and the reason split have _nonzero variants for the same reason
    // the other pre-summed columns do: excludeZeroApptDays is a row-level filter
    // that a summed week cannot honour after the fact.
    totalMedRev += num(
      useNonzeroColumns ? r.medical_net_sales_amount_nonzero : r.medical_net_sales_amount
    );
    totalCancelLeadDays += num(
      useNonzeroColumns
        ? r.canceled_appointment_lead_time_days_nonzero
        : r.canceled_appointment_lead_time_days
    );
    for (const reason of CANCEL_REASONS) {
      canceledByReason[reason] += num(
        r[
          useNonzeroColumns
            ? `canceled_appointments_${reason}_nonzero`
            : `canceled_appointments_${reason}`
        ]
      );
    }
  };

  const scopedSiteWeeks = siteWeeks.filter((r) => matchesScope(r, scope));
  const scopedDvmWeeks = dvmWeeks.filter((r) => matchesScope(r, scope));

  for (const r of measuresFromDvmWeeks ? scopedDvmWeeks : scopedSiteWeeks) {
    addMeasures(r as Record<string, unknown>, nz);
  }

  // ── Distinct counts, from the masks ───────────────────────────────────────
  // OR the masks per (doctor, week) BEFORE counting bits. A doctor at two sites
  // on the same Tuesday produces two rows with that bit set; counting per row and
  // summing would count the day twice. OR collapses it, which is what makes this
  // exact rather than approximate.
  const maskOf = (r: DvmWeekRow) => (nz ? r.day_mask_nonzero : r.day_mask);
  const perDvmWeek = new Map<string, number>();
  const perWeek = new Map<string, number>();
  const dvmSet = new Set<string>();
  const siteSet = new Set<string>();

  for (const r of scopedDvmWeeks) {
    const m = maskOf(r);
    // Mask 0 means the doctor contributed no qualifying day that week — under
    // excludeZeroApptDays, a week with no appointments at all. It must not count
    // as a DVM-week either, or the per-week denominators inflate.
    if (m === 0) continue;
    const dw = `${r.resource_id}|${r.week_start}`;
    perDvmWeek.set(dw, (perDvmWeek.get(dw) ?? 0) | m);
    perWeek.set(r.week_start, (perWeek.get(r.week_start) ?? 0) | m);
    dvmSet.add(r.resource_id);
    siteSet.add(r.location_id);
  }

  let dvmDays = 0;
  for (const m of perDvmWeek.values()) dvmDays += popcount7(m);
  let distinctDates = 0;
  for (const m of perWeek.values()) distinctDates += popcount7(m);
  let dvmWeekCount = perDvmWeek.size;

  // ── The ragged edge ───────────────────────────────────────────────────────
  // Disjoint by day from the whole weeks, so day-based counts add. Doctors and
  // sites can repeat across the two halves, so those are unions.
  const edgeScoped = edgeRows.filter((r) => matchesScope(r, scope));
  const edgeKept = nz
    ? edgeScoped.filter((r) => Number(r.completed_appointment_minutes) > 0)
    : edgeScoped;

  if (edgeKept.length) {
    const edgeDvmDays = new Set<string>();
    const edgeDates = new Set<string>();
    const edgeDvmWeeks = new Set<string>();
    for (const r of edgeKept) {
      // Raw rows have no _nonzero columns; the filter above already applied the
      // exclusion, so the plain columns are correct here.
      addMeasures(r as unknown as Record<string, unknown>, false);
      edgeDvmDays.add(`${r.resource_id}|${r.schedule_date}`);
      edgeDates.add(r.schedule_date);
      edgeDvmWeeks.add(`${r.resource_id}|${getWeekKey(r.schedule_date)}`);
      dvmSet.add(r.resource_id);
      siteSet.add(r.location_id);
    }
    dvmDays += edgeDvmDays.size;
    distinctDates += edgeDates.size;
    dvmWeekCount += edgeDvmWeeks.size;
  }

  return {
    siteCount: siteSet.size,
    dvmDays,
    distinctDvms: dvmSet.size,
    dvmWeeks: dvmWeekCount,
    distinctDates,
    totalScheduledMin,
    totalWorkedMin,
    totalBlockoffMin,
    allSeenMin,
    totalAvailMin,
    slugMinutes,
    totalBookableBlocks,
    totalMultiBookedBlocks,
    totalCompleted,
    totalCanceled,
    totalMedRev,
    canceledByReason,
    totalCancelLeadDays,
  };
}
